import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import { ArrowRight, Calendar, Instagram } from "lucide-react";
import WhatsAppCTA from "../components/WhatsAppCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const demoPosts = [
  {
    id: "1",
    title: "Cómo elegir la vegetación correcta para el clima de Los Cabos",
    slug: "vegetacion-correcta-los-cabos",
    excerpt: "Guía técnica para seleccionar plantas que prosperan en el microclima único del desierto tropical.",
    content: "",
    cover_image: "/assets/projects/jardin-nacho/03_dsc00509.jpg",
    tags: ["Vegetación", "Diseño", "Los Cabos"],
    published: true,
    created_at: "2026-01-15T10:00:00Z"
  },
  {
    id: "2",
    title: "Errores comunes en el diseño de jardines desérticos",
    slug: "errores-jardines-deserticos",
    excerpt: "Evita los 5 errores más frecuentes que comprometen la salud y estética de tu paisaje.",
    content: "",
    cover_image: "/assets/projects/kitchen/03_dsc02536.jpg",
    tags: ["Errores", "Diseño", "Consejos"],
    published: true,
    created_at: "2026-01-10T10:00:00Z"
  },
  {
    id: "3",
    title: "La importancia del estudio de suelo antes de diseñar",
    slug: "importancia-estudio-suelo",
    excerpt: "Por qué un análisis técnico del sitio es fundamental para el éxito a largo plazo de tu paisaje.",
    content: "",
    cover_image: "/assets/projects/casa-roca/img_20231128_103712.jpg",
    tags: ["Estudio de Suelo", "Técnico", "Planificación"],
    published: true,
    created_at: "2026-01-05T10:00:00Z"
  }
];

const BlogCard = ({ post, index }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
    >
      <Link 
        to={`/blog/${post.slug}`}
        className="blog-card block group"
        data-testid={`blog-card-${post.slug}`}
      >
        <div className="blog-card-image">
          <img src={post.cover_image} alt={post.title} />
        </div>
        <div className="p-6">
          <div className="flex flex-wrap gap-2 mb-3">
            {post.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="tag text-[10px]">{tag}</span>
            ))}
          </div>
          <h3 className="text-xl font-serif mb-3 group-hover:text-circulo-sand transition-colors line-clamp-2">
            {post.title}
          </h3>
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
            {post.excerpt}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Calendar size={14} />
              {formatDate(post.created_at)}
            </div>
            <span className="text-xs text-circulo-sand flex items-center gap-1 group-hover:gap-2 transition-all">
              Leer más
              <ArrowRight size={12} />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API}/blog?published=true`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data.length > 0 ? data : demoPosts);
      } else {
        setPosts(demoPosts);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
      setPosts(demoPosts);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 bg-[#F9F6F0]" data-testid="blog-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs tracking-[0.3em] uppercase text-circulo-sand mb-4 block"
          >
            Conocimiento
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium mb-6"
          >
            Blog
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6"
          >
            <p className="text-lg text-muted-foreground max-w-2xl">
              Artículos sobre paisajismo, selección vegetal y diseño exterior en el clima de Los Cabos.
            </p>
            <a 
              href="https://www.instagram.com/circle.naturaledit" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-circulo-sand transition-colors"
            >
              <Instagram size={18} />
              Síguenos en Instagram
            </a>
          </motion.div>
        </div>

        {/* Posts Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-video bg-circulo-subtle mb-4" />
                <div className="h-4 bg-circulo-subtle w-1/3 mb-3" />
                <div className="h-6 bg-circulo-subtle w-full mb-2" />
                <div className="h-4 bg-circulo-subtle w-2/3" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              <BlogCard key={post.id} post={post} index={index} />
            ))}
          </div>
        )}

        {posts.length === 0 && !loading && (
          <div className="text-center py-16">
            <p className="text-muted-foreground">No hay artículos publicados aún.</p>
          </div>
        )}

        {/* WhatsApp CTA */}
        <div className="mt-20">
          <WhatsAppCTA
            source="blog_page"
            title="¿Tienes dudas sobre tu jardín?"
            subtitle="Escríbenos por WhatsApp y te orientamos sin compromiso."
            buttonText="Consultar por WhatsApp"
            message="Hola, leí su blog y tengo una consulta sobre mi jardín."
            variant="light"
          />
        </div>
      </div>
    </div>
  );
}
